import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import axios from "axios";
import { follow, unfollow } from "../../functions/user";
import "../../mobile-responsive.css";
import { FaComment, FaShare, FaEllipsisH, FaBolt, FaBook, FaLink, FaUsers, FaBookmark, FaPaperclip, FaHeart, FaRegHeart } from "react-icons/fa";
import { FaWhatsapp } from "react-icons/fa";
// Play/Pause button for shorts video
function PlayPauseButton({ videoRef }) {
  const [playing, setPlaying] = useState(true);
  useEffect(() => {
    const vid = videoRef?.current;
    if (!vid) return;
    const update = () => setPlaying(!vid.paused);
    update();
    vid.addEventListener("play", update);
    vid.addEventListener("pause", update);
    return () => {
      vid.removeEventListener("play", update);
      vid.removeEventListener("pause", update);
    };
  }, [videoRef]);
  const handleToggle = () => {
    const vid = videoRef?.current;
    if (!vid) return;
    if (vid.paused) vid.play().catch(()=>{}); else vid.pause();
  };
  return (
    <button
      onClick={handleToggle}
      style={{
        position: "absolute",
        left: 16,
        top: 16,
        background: "transparent",
        color: "#1976d2",
        border: "none",
        borderRadius: "50%",
        width: 32,
        height: 32,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 18,
        boxShadow: "0 2px 8px #0002",
        cursor: "pointer",
        zIndex: 5
      }}
      title={playing ? "Pause" : "Play"}
    >
      {playing ? "⏸" : "▶️"}
    </button>
  );
}

// Custom progress bar component with scrolling/clicking functionality
function VideoProgressBar({ videoId, containerStyle }) {
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [hoverPosition, setHoverPosition] = useState(0);
  
  useEffect(() => {
    const vid = document.getElementById(videoId);
    if (!vid) return;
    
    let lastUpdateTime = 0;
    const updateInterval = 100; // Update every 100ms instead of every timeupdate event
    
    const update = () => {
      const now = Date.now();
      if (now - lastUpdateTime >= updateInterval) {
        setProgress(vid.duration ? vid.currentTime / vid.duration : 0);
        setCurrentTime(vid.currentTime || 0);
        lastUpdateTime = now;
      }
    };
    
    const handleTimeUpdate = () => {
      requestAnimationFrame(update);
    };
    
    vid.addEventListener('timeupdate', handleTimeUpdate);
    vid.addEventListener('loadedmetadata', update);
    return () => {
      vid.removeEventListener('timeupdate', handleTimeUpdate);
      vid.removeEventListener('loadedmetadata', update);
    };
  }, [videoId]);
  
  // Helper to format duration as mm:ss
  const formatDuration = (d) => {
    if (!d || isNaN(d)) return "";
    const min = Math.floor(d / 60);
    const sec = Math.floor(d % 60);
    return `${min}:${sec.toString().padStart(2, "0")}`;
  };
  
  // Handle click/touch on progress bar to seek
  const handleProgressClick = (e) => {
    e.preventDefault();
    const vid = document.getElementById(videoId);
    if (!vid || !vid.duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.type === 'touchend' ? e.changedTouches[0].clientX : e.clientX;
    const clickX = clientX - rect.left;
    const clickPercent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = clickPercent * vid.duration;
    
    // Smooth seeking with a small delay to prevent rapid updates
    setTimeout(() => {
      vid.currentTime = newTime;
      setProgress(clickPercent);
      setCurrentTime(newTime);
    }, 50);
  };
  
  // Handle mouse move for hover preview with throttling
  const handleMouseMove = (e) => {
    requestAnimationFrame(() => {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const hoverPercent = Math.max(0, Math.min(1, mouseX / rect.width));
      setHoverPosition(hoverPercent);
    });
  };
  
  let duration = "";
  const videoElem = typeof window !== "undefined" ? document.getElementById(videoId) : null;
  if (videoElem && videoElem.duration) {
    duration = formatDuration(videoElem.duration);
  }
  
  return (
    <div style={containerStyle}>
      {/* Show current time / total duration above the bar */}
      <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: 2 }}>
        {duration && (
          <span style={{ color: "#fff", fontWeight: 500, fontSize: 13, background: "rgba(0,0,0,0.35)", borderRadius: 6, padding: "2px 8px" }}>
            {formatDuration(currentTime)} / {duration}
          </span>
        )}
      </div>
      
      {/* Progress bar container */}
      <div 
        style={{ 
          width: "100%", 
          height: 8, 
          background: "#fff3", 
          borderRadius: 4, 
          overflow: "hidden", 
          position: "relative",
          cursor: "pointer",
          transition: "height 0.2s"
        }}
        onClick={handleProgressClick}
        onTouchEnd={handleProgressClick}
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {/* Background track */}
        <div style={{ 
          width: "100%", 
          height: "100%", 
          background: isHovering ? "#fff4" : "#fff3", 
          borderRadius: 4 
        }} />
        
        {/* Progress fill */}
        <div style={{ 
          width: `${progress * 100}%`, 
          height: "100%", 
          background: isHovering ? "#1565c0" : "#1976d2", 
          borderRadius: 4, 
          transition: "width 0.3s ease-out, background 0.2s",
          position: "absolute",
          top: 0,
          left: 0
        }} />
        
        {/* Hover preview */}
        {isHovering && (
          <div style={{
            position: "absolute",
            left: `${hoverPosition * 100}%`,
            top: 0,
            width: 2,
            height: "100%",
            background: "#fff",
            borderRadius: 1,
            transform: "translateX(-50%)",
            boxShadow: "0 0 4px rgba(255,255,255,0.5)"
          }} />
        )}
        
        {/* Progress handle/thumb */}
        <div style={{
          position: "absolute",
          left: `${progress * 100}%`,
          top: "50%",
          width: isHovering ? 12 : 8,
          height: isHovering ? 12 : 8,
          background: "#fff",
          borderRadius: "50%",
          transform: "translate(-50%, -50%)",
          transition: "left 0.3s ease-out, width 0.2s, height 0.2s",
          boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
          border: "2px solid #1976d2"
        }} />
      </div>
    </div>
  );
}

const mapCommentsForWatch = (comments = []) =>
  (comments || [])
    .filter((c) => !c?.isDeleted && String(c?.comment || "").trim())
    .map((c) => {
      const firstName = c?.commentBy?.first_name || "";
      const lastName = c?.commentBy?.last_name || "";
      const fullName = `${firstName} ${lastName}`.trim();
      const username = c?.commentBy?.username || "";
      const replies = (c?.replies || [])
        .filter((r) => String(r?.comment || "").trim())
        .map((r) => {
          const replyFirstName = r?.commentBy?.first_name || "";
          const replyLastName = r?.commentBy?.last_name || "";
          const replyFullName = `${replyFirstName} ${replyLastName}`.trim();
          const replyUsername = r?.commentBy?.username || "";
          return {
            id: r?._id || `${r?.commentAt || ""}-${r?.commentBy?._id || ""}`,
            text: r?.comment || "",
            displayName: replyFirstName || replyLastName || replyFullName || replyUsername || "User",
            commentAt: r?.commentAt,
          };
        });

      return {
        id: c?._id || `${c?.commentAt || ""}-${c?.commentBy?._id || ""}`,
        text: c?.comment || "",
        displayName: firstName || lastName || fullName || username || "User",
        commentAt: c?.commentAt,
        replies,
      };
    });

const formatCommentAge = (dateValue) => {
  if (!dateValue) return "";
  const diffMs = Date.now() - new Date(dateValue).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return "";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins} m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} d`;
};

function Watch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useSelector((state) => ({ ...state }));
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("all");
  const [videoDurations, setVideoDurations] = useState({}); // { url: duration }

  // Add data attribute to body when shorts is active for CSS targeting
  useEffect(() => {
    if (category === "shorts") {
      document.body.setAttribute("data-shorts-active", "true");
    } else {
      document.body.removeAttribute("data-shorts-active");
    }
  }, [category]);

  // Set category and update body attribute for mobile responsive hiding
  useEffect(() => {
    document.body.setAttribute('data-category', category);
  }, [category]);


  // Handle category change and update body attribute
  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
    // Register user interaction when clicking category buttons
    setHasUserInteracted(true);
    // Scroll to top when switching categories for better UX
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Preload all videos when "All Videos" is clicked
    if (newCategory === "all") {
      setTimeout(() => {
        const allVideoElements = document.querySelectorAll('video');
        allVideoElements.forEach((video) => {
          // Preload metadata and try to load video
          video.preload = 'metadata';
          video.load();
          // Load a small portion of the video for better preview
          if (video.readyState < 2) {
            video.preload = 'auto';
            video.load();
          }
        });
      }, 100);
    }
  };

  // Handle go back to All Videos from Movies page
  const handleMoviesGoBack = () => {
    setCategory("all"); // Go back to "All Videos" category
  };

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        if (!user || !user.token) {
          setError("User not authenticated");
          setLoading(false);
          return;
        }
        // Fetch all posts with auth
        const { data } = await axios.get("/getAllPosts", {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });
        // Extract all video URLs from posts and initialize engagement state from server
        const videoList = [];
        const initialEngagementByPost = {};
        const currentUserId = String(user?.id || user?._id || "");

        data.forEach((post) => {
          const postId = String(post._id);
          const likeUsers = Array.isArray(post.likes) ? post.likes : [];
          const likedByMe = likeUsers.some((id) => String(id?._id || id) === currentUserId);

          initialEngagementByPost[postId] = {
            likesCount: likeUsers.length,
            likedByMe,
            comments: mapCommentsForWatch(post.comments || []),
          };

          if (post.images && Array.isArray(post.images)) {
            post.images.forEach((img) => {
              if (img.type === "video") {
                videoList.push({
                  url: img.url,
                  postId,
                  userId: String(post.user?._id || post.user?.id || ""),
                  username: post.user?.username || post.user?.name || "Unknown",
                  profile: post.user?.picture || post.user?.profilePic || null,
                });
              }
            });
          }
        });

        const currentFollowingIds = Array.isArray(user.following)
          ? user.following.map((id) => String(id?._id || id))
          : [];
        const initialFollowing = {};
        videoList.forEach((video) => {
          if (video.userId && currentFollowingIds.includes(video.userId)) {
            initialFollowing[video.userId] = true;
          }
        });

        setVideos(videoList);
        setFollowing((prev) => ({ ...initialFollowing, ...prev }));

        // Sync likes from reacts collection so counts match across all users
        const reactionEntries = await Promise.all(
          Object.keys(initialEngagementByPost).map(async (postId) => {
            try {
              const { data: reactData } = await axios.get(`/getReacts/${postId}`, {
                headers: {
                  Authorization: `Bearer ${user.token}`,
                },
              });

              return [
                postId,
                {
                  ...initialEngagementByPost[postId],
                  likesCount: Number(reactData?.total || 0),
                  likedByMe: reactData?.check === "like",
                },
              ];
            } catch (reactionError) {
              return [postId, initialEngagementByPost[postId]];
            }
          })
        );

        setEngagementByPost(Object.fromEntries(reactionEntries));
      } catch (err) {
        setError("Failed to load videos");
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, [user]);

  // Preload videos when category is "all"
  useEffect(() => {
    if (category === "all" && videos.length > 0) {
      // Preload all videos when on "All Videos" page
      const preloadVideos = async () => {
        // Use a more comprehensive selector to find all video elements in the watch container
        const watchContainer = document.querySelector('.watch-container');
        if (!watchContainer) return;
        
        const videoElements = watchContainer.querySelectorAll('video');
        videoElements.forEach((video) => {
          if (video instanceof HTMLVideoElement) {
            // Set preload to auto for better loading
            if (video.preload !== 'auto') {
              video.preload = 'auto';
            }
            // Force load the video
            video.load();
            
            // Try to load metadata immediately
            if (video.readyState < 1) {
              video.preload = 'metadata';
              video.load();
            }
          }
        });
      };
      // Delay to ensure DOM is ready, then try again after a short delay
      const timer1 = setTimeout(preloadVideos, 100);
      const timer2 = setTimeout(preloadVideos, 500);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [category, videos]);

  // Handle video duration loading
  useEffect(() => {
    // Only check durations for videos that don't have it yet
    const toCheck = videos.filter(v => videoDurations[v.url] === undefined);
    if (toCheck.length === 0) return;

    const loadDurations = async () => {
      const promises = toCheck.map(v => {
        return new Promise((resolve) => {
          const vid = document.createElement('video');
          vid.src = v.url;
          vid.preload = 'metadata';
          vid.onloadedmetadata = () => {
            const duration = vid.duration;
            if (duration && !isNaN(duration) && isFinite(duration)) {
              resolve({ url: v.url, duration: duration });
            } else {
              // If duration is invalid, assume it's a short
              resolve({ url: v.url, duration: 30 });
            }
          };
          vid.oncanplay = () => {
            const duration = vid.duration;
            if (duration && !isNaN(duration) && isFinite(duration)) {
              resolve({ url: v.url, duration: duration });
            }
          };
          vid.onerror = () => {
            // If video fails to load, assume it's a short video
            resolve({ url: v.url, duration: 30 });
          };
          // Timeout after 8 seconds (increased to give more time for loading)
          setTimeout(() => {
            resolve({ url: v.url, duration: 30 });
          }, 8000);
        });
      });

      const results = await Promise.all(promises);
      const newDurations = {};
      results.forEach(({ url, duration }) => {
        newDurations[url] = duration;
      });
      setVideoDurations(prev => ({ ...prev, ...newDurations }));
    };

    loadDurations();
  }, [videos, videoDurations]);

  // Categorize videos by duration
  const shorts = videos.filter(v => {
    const duration = videoDurations[v.url];
    // Include videos that are:
    // 1. Successfully detected as less than 60 seconds, OR
    // 2. Duration detection failed (undefined/NaN) - assume they are shorts by default
    return duration === undefined || isNaN(duration) || duration < 60;
  });
  const movies = videos.filter(v => {
    const duration = videoDurations[v.url];
    // Only include videos that are definitely 60 seconds or longer
    // Exclude undefined durations and shorts
    return duration !== undefined && !isNaN(duration) && duration >= 60;
  });
  const allVideos = videos;

  // Debug logging to help identify Progasper's videos
  // console.log(`Total videos: ${videos.length}, Shorts: ${shorts.length}, Movies: ${movies.length}`);
  const progasperVideos = videos.filter(v => v.username && v.username.toLowerCase().includes('progasper'));
  // console.log(`Progasper videos found: ${progasperVideos.length}`);
  progasperVideos.forEach(v => {
    const duration = videoDurations[v.url];
    console.log(`Progasper video: ${v.url}, duration: ${duration}, in shorts: ${shorts.some(s => s.url === v.url)}`);
  });

  let displayVideos = allVideos;
  if (category === "shorts") displayVideos = shorts;
  else if (category === "movies") displayVideos = movies;
  else displayVideos = allVideos;

  // Handle URL parameters for direct video navigation
  useEffect(() => {
    const videoId = searchParams.get('video');
    const videoType = searchParams.get('type');
    
    if (videoId && videos.length > 0) {
      const targetVideo = videos.find(v => v.postId === videoId);
      if (targetVideo) {
        if (videoType === 'shorts') {
          const shortsIndex = shorts.findIndex(s => s.postId === videoId);
          if (shortsIndex !== -1) {
            setCategory('shorts');
            setShortIndex(shortsIndex);
          }
        } else if (videoType === 'movies') {
          setCategory('movies');
          setFullScreenVideo(targetVideo);
        } else {
          // Auto-detect based on duration
          const duration = videoDurations[targetVideo.url];
          if (duration && duration < 60) {
            const shortsIndex = shorts.findIndex(s => s.postId === videoId);
            if (shortsIndex !== -1) {
              setCategory('shorts');
              setShortIndex(shortsIndex);
            }
          } else {
            setCategory('movies');
            setFullScreenVideo(targetVideo);
          }
        }
      }
    }
  }, [searchParams, videos, shorts, videoDurations]);

  // Shorts state for like/comments
  const [shortIndex, setShortIndex] = useState(0);
  const [engagementByPost, setEngagementByPost] = useState({}); // { postId: { likesCount, likedByMe, comments } }
  
  const [shares, setShares] = useState(() => {
    try {
      const saved = localStorage.getItem('shortsShares');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading shares:', error);
      return {};
    }
  });
  
  const [showComments, setShowComments] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [likedComments, setLikedComments] = useState(() => {
    try {
      const saved = localStorage.getItem("watchCommentLikes");
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Error loading comment likes:", error);
      return {};
    }
  }); // { [commentId]: boolean }
  const [showReplyForComment, setShowReplyForComment] = useState(null); // commentId
  const [replyDraftByComment, setReplyDraftByComment] = useState({}); // { [commentId]: string }
  const [newComment, setNewComment] = useState("");
  // Shorts mute state
  const [isMuted, setIsMuted] = useState(true);
  // Mute state for all videos (row/grid): { url: boolean }
  const [videoMute, setVideoMute] = useState({});
  // Pause state for all videos (row/grid): { url: boolean }
  const [, setVideoPause] = useState({});
  // Track if user has interacted with the page
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  // Full screen video player state
  const [fullScreenVideo, setFullScreenVideo] = useState(null);
  // Follow state for users: { [userId]: boolean }
  const [following, setFollowing] = useState({});
  const [followLoading, setFollowLoading] = useState({});
  // Shorts three dots menu state
  const [showShortsMenu, setShowShortsMenu] = useState(null); // { short, position }
  // Saved shorts state - load from localStorage
  const [savedShorts, setSavedShorts] = useState(() => {
    try {
      const saved = localStorage.getItem('savedShorts');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading saved shorts:', error);
      return [];
    }
  });

  // Persist comment likes across refresh
  useEffect(() => {
    try {
      localStorage.setItem("watchCommentLikes", JSON.stringify(likedComments));
    } catch (error) {
      console.error("Error saving comment likes:", error);
    }
  }, [likedComments]);

  // Ref for active shorts video
  const shortVideoRef = useRef(null);
  // Navigation throttle lock for shorts scrolling
  const navigationLockRef = useRef(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );

  // Add user interaction listeners
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      // Try to play the current video if it's paused
      const vid = document.getElementById("shorts-video");
      if (vid && vid.paused) {
        vid.play().catch(() => {}); // Suppress error
      }
    };

    // Listen for any user interaction
    document.addEventListener('click', handleUserInteraction, { once: true });
    document.addEventListener('keydown', handleUserInteraction, { once: true });
    document.addEventListener('touchstart', handleUserInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // Track viewport size for fullscreen modal responsive layout
  useEffect(() => {
    const onResize = () => {
      setIsMobileViewport(window.innerWidth <= 900);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Handle video playback when switching between shorts
  useEffect(() => {
    if (category === "shorts" && hasUserInteracted) {
      const vid = shortVideoRef.current;
      if (vid) {
        // Pause all other videos first
        const allVideos = document.querySelectorAll('video');
        allVideos.forEach(v => {
          if (v !== vid) {
            v.pause();
            v.currentTime = 0;
          }
        });
        
        // Play the current video after a short delay
        setTimeout(() => {
          vid.currentTime = 0; // Reset to beginning
          vid.play().catch((error) => {
            console.log('Auto-play prevented:', error);
            // If autoplay fails, show a play button or handle gracefully
          });
        }, 100);
      }
    }
  }, [shortIndex, category, hasUserInteracted]);

  // Handle scrolling to specific video when navigating from All Videos
  useEffect(() => {
    if (category === "shorts" && shorts.length > 0) {
      // Small delay to ensure the DOM is updated
      setTimeout(() => {
        // Find the container div for the specific short
        const shortsContainer = document.querySelector('.shorts-scroll-container');
        if (shortsContainer && shortIndex >= 0) {
          // Get all short item containers
          const shortItems = shortsContainer.querySelectorAll('.shorts-item-container');
          if (shortItems[shortIndex]) {
            shortItems[shortIndex].scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'center'
            });
          }
        }
      }, 200);
    }
  }, [category, shortIndex, shorts.length]);

  // Auto-advance to next short when video ends
  useEffect(() => {
    if (category !== "shorts" || shorts.length === 0) return;
    
    const vid = shortVideoRef.current;
    if (!vid) return;

    const handleVideoEnded = () => {
      console.log('Video ended, auto-advancing...');
      // Auto-advance to next short
      if (shortIndex < shorts.length - 1) {
        setShortIndex(prevIndex => {
          console.log(`Advancing from ${prevIndex} to ${prevIndex + 1}`);
          return prevIndex + 1;
        });
        // Show a brief notification
        showAutoAdvanceNotification();
      } else {
        // If it's the last short, loop back to the first one
        console.log('Last short reached, looping back to first');
        setShortIndex(0);
        showAutoAdvanceNotification();
      }
    };

    const handlePlay = () => {
      setVideoPause(prev => ({ ...prev, [shorts[shortIndex]?.url]: false }));
    };

    const handlePause = () => {
      setVideoPause(prev => ({ ...prev, [shorts[shortIndex]?.url]: true }));
    };

    // Add event listeners
    vid.addEventListener('ended', handleVideoEnded);
    vid.addEventListener('play', handlePlay);
    vid.addEventListener('pause', handlePause);
    
    return () => {
      // Clean up event listeners
      vid.removeEventListener('ended', handleVideoEnded);
      vid.removeEventListener('play', handlePlay);
      vid.removeEventListener('pause', handlePause);
    };
  }, [category, shorts.length, shortIndex, shorts]);

  // Show auto-advance notification
  const showAutoAdvanceNotification = () => {
    // Create a temporary notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      background: rgba(25, 118, 210, 0.9);
      color: white;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 500;
      z-index: 10000;
      animation: fadeInOut 2s ease-in-out;
    `;
    notification.textContent = '⏭️ Auto-advancing to next short...';
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes fadeInOut {
        0% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
        20% { opacity: 1; transform: translateX(-50%) translateY(0); }
        80% { opacity: 1; transform: translateX(-50%) translateY(0); }
        100% { opacity: 0; transform: translateX(-50%) translateY(-10px); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    // Remove after animation
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 2000);
  };

  // Show reel saved notification
  const showReelSavedNotification = (message) => {
    // Create a styled notification element
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: white;
      color: #333;
      padding: 20px 30px;
      border-radius: 12px;
      font-size: 16px;
      font-weight: 500;
      z-index: 10000;
      box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      border: 1px solid #e0e0e0;
      text-align: center;
      min-width: 200px;
      animation: reelSavedFadeInOut 3s ease-in-out;
    `;
    notification.textContent = message;
    
    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes reelSavedFadeInOut {
        0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
        15% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        85% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        100% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
      }
    `;
    document.head.appendChild(style);
    
    document.body.appendChild(notification);
    
    // Remove after animation
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    }, 3000);
  };

  // --- Shorts scroll/swipe/keyboard navigation ---
  useEffect(() => {
    if (category !== "shorts" || shorts.length === 0) return;
    let lastTouchY = null;
    let touchMoved = false;
    const COOLDOWN_MS = 450;

    const triggerNavigation = (nextIndex) => {
      if (navigationLockRef.current) return;
      navigationLockRef.current = true;
      setShortIndex(nextIndex);
      setTimeout(() => { navigationLockRef.current = false; }, COOLDOWN_MS);
    };

    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) < 10) return;
      if (navigationLockRef.current) { e.preventDefault(); return; }
      if (e.deltaY > 0 && shortIndex < shorts.length - 1) {
        e.preventDefault();
        triggerNavigation(Math.min(shortIndex + 1, shorts.length - 1));
      } else if (e.deltaY < 0 && shortIndex > 0) {
        e.preventDefault();
        triggerNavigation(Math.max(shortIndex - 1, 0));
      }
    };

    const handleKeyDown = (e) => {
      if (navigationLockRef.current) return;
      if (e.key === "ArrowDown" && shortIndex < shorts.length - 1) {
        e.preventDefault?.();
        triggerNavigation(Math.min(shortIndex + 1, shorts.length - 1));
      } else if (e.key === "ArrowUp" && shortIndex > 0) {
        e.preventDefault?.();
        triggerNavigation(Math.max(shortIndex - 1, 0));
      }
    };

    const handleTouchStart = (e) => {
      if (e.touches && e.touches.length === 1) {
        lastTouchY = e.touches[0].clientY;
        touchMoved = false;
      }
    };
    const handleTouchMove = (e) => {
      if (lastTouchY === null) return;
      const currentY = e.touches[0].clientY;
      const diff = currentY - lastTouchY;
      if (Math.abs(diff) > 40 && !touchMoved) {
        if (navigationLockRef.current) { e.preventDefault(); return; }
        if (diff < 0 && shortIndex < shorts.length - 1) {
          e.preventDefault();
          triggerNavigation(Math.min(shortIndex + 1, shorts.length - 1));
          touchMoved = true;
        } else if (diff > 0 && shortIndex > 0) {
          e.preventDefault();
          triggerNavigation(Math.max(shortIndex - 1, 0));
          touchMoved = true;
        }
      }
    };
    const handleTouchEnd = () => {
      lastTouchY = null;
      touchMoved = false;
    };

    const container = document.getElementById("shorts-scroll-container");
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      container.addEventListener("touchstart", handleTouchStart, { passive: true });
      container.addEventListener("touchmove", handleTouchMove, { passive: false });
      container.addEventListener("touchend", handleTouchEnd, { passive: true });
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
        container.removeEventListener("touchstart", handleTouchStart);
        container.removeEventListener("touchmove", handleTouchMove);
        container.removeEventListener("touchend", handleTouchEnd);
      }
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [category, shorts.length, shortIndex]);

  // Toggle like for shorts using server state
  const handleLike = async (short) => {
    if (!short?.postId || !user?.token) return;

    try {
      await axios.put(
        "/reactPost",
        { postId: short.postId, react: "like" },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const { data } = await axios.get(`/getReacts/${short.postId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });

      setEngagementByPost((prev) => ({
        ...prev,
        [short.postId]: {
          ...(prev[short.postId] || { comments: [] }),
          likesCount: Number(data?.total || 0),
          likedByMe: data?.check === "like",
        },
      }));
    } catch (likeError) {
      console.error("Error updating like:", likeError);
    }
  };
  // Handle share for shorts
  const handleShare = (url) => {
    // Show share dialog
    setShowShareDialog(true);
  };
  // Handle actual sharing (increment count)
  const handleActualShare = (url) => {
    setShares(prev => {
      const newShares = {
        ...prev,
        [url]: (prev[url] || 0) + 1
      };
      // Save to localStorage
      try {
        localStorage.setItem('shortsShares', JSON.stringify(newShares));
      } catch (error) {
        console.error('Error saving shares:', error);
      }
      return newShares;
    });
    // Create shareable link with video ID
    const video = videos.find(v => v.url === url);
    const shareableUrl = video ? `${window.location.origin}/watch?video=${video.postId}&type=shorts` : url;
    navigator.clipboard.writeText(shareableUrl);
    // Close dialog
    setShowShareDialog(false);
  };
  
  // Handle specific share options
  const handleMessengerShare = (url) => {
    setShares(prev => ({
      ...prev,
      [url]: (prev[url] || 0) + 1
    }));
    // Create shareable link with video ID
    const video = videos.find(v => v.url === url);
    const shareableUrl = video ? `${window.location.origin}/watch?video=${video.postId}&type=shorts` : url;
    window.open(`https://m.me/share?url=${encodeURIComponent(shareableUrl)}`, '_blank');
    setShowShareDialog(false);
  };
  
  const handleWhatsAppShare = (url) => {
    setShares(prev => ({
      ...prev,
      [url]: (prev[url] || 0) + 1
    }));
    // Create shareable link with video ID
    const video = videos.find(v => v.url === url);
    const shareableUrl = video ? `${window.location.origin}/watch?video=${video.postId}&type=shorts` : url;
    window.open(`https://wa.me/?text=${encodeURIComponent(shareableUrl)}`, '_blank');
    setShowShareDialog(false);
  };
  
  const handleStoryShare = (url) => {
    setShares(prev => ({
      ...prev,
      [url]: (prev[url] || 0) + 1
    }));
    // Create shareable link with video ID
    const video = videos.find(v => v.url === url);
    const shareableUrl = video ? `${window.location.origin}/watch?video=${video.postId}&type=shorts` : url;
    navigator.clipboard.writeText(shareableUrl);
    alert('Link copied! You can now paste it in your story.');
    setShowShareDialog(false);
  };
  
  const handleFriendsShare = (url) => {
    setShares(prev => ({
      ...prev,
      [url]: (prev[url] || 0) + 1
    }));
    // Create shareable link with video ID
    const video = videos.find(v => v.url === url);
    const shareableUrl = video ? `${window.location.origin}/watch?video=${video.postId}&type=shorts` : url;
    navigator.clipboard.writeText(shareableUrl);
    alert('Link copied! You can now share it with your friends.');
    setShowShareDialog(false);
  };
  // Add comment via server so all users can see it (with username)
  const handleAddComment = async (short) => {
    const text = newComment.trim();
    if (!text || !short?.postId || !user?.token) return;

    try {
      const { data } = await axios.put(
        "/comment",
        {
          postId: short.postId,
          comment: text,
          image: "",
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      setEngagementByPost((prev) => ({
        ...prev,
        [short.postId]: {
          ...(prev[short.postId] || { likesCount: 0, likedByMe: false }),
          comments: mapCommentsForWatch(Array.isArray(data) ? data : []),
        },
      }));

      setNewComment("");
    } catch (commentError) {
      console.error("Error adding comment:", commentError);
    }
  };

  const handleToggleCommentLike = (commentId) => {
    if (!commentId) return;
    setLikedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handleReplyToComment = async (short, commentId) => {
    const replyText = (replyDraftByComment[commentId] || "").trim();
    if (!replyText || !short?.postId || !user?.token || !commentId) return;

    try {
      const { data } = await axios.put(
        "/replyToComment",
        {
          postId: short.postId,
          parentCommentId: commentId,
          reply: replyText,
          image: "",
        },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      const mappedReplies = (data || []).map((r) => {
        const replyFirstName = r?.commentBy?.first_name || "";
        const replyLastName = r?.commentBy?.last_name || "";
        const replyFullName = `${replyFirstName} ${replyLastName}`.trim();
        const replyUsername = r?.commentBy?.username || "";
        return {
          id: r?._id || `${r?.commentAt || ""}-${r?.commentBy?._id || ""}`,
          text: r?.comment || "",
          displayName: replyFirstName || replyLastName || replyFullName || replyUsername || "User",
          commentAt: r?.commentAt,
        };
      });

      setEngagementByPost((prev) => {
        const postEngagement = prev[short.postId] || { likesCount: 0, likedByMe: false, comments: [] };
        const updatedComments = (postEngagement.comments || []).map((c) =>
          c.id === commentId ? { ...c, replies: mappedReplies } : c
        );

        return {
          ...prev,
          [short.postId]: {
            ...postEngagement,
            comments: updatedComments,
          },
        };
      });

      setReplyDraftByComment((prev) => ({ ...prev, [commentId]: "" }));
      setShowReplyForComment(null);
    } catch (replyError) {
      console.error("Error adding reply:", replyError);
    }
  };

  useEffect(() => {
    const loadCurrentFollowList = async () => {
      if (!user?.token || !user?.username) return;
      if (Array.isArray(user.following) && user.following.length > 0) return;

      try {
        const { data } = await axios.get(`/getProfile/${user.username}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        const serverFollowing = Array.isArray(data.following)
          ? data.following.map((id) => String(id?._id || id))
          : [];

        const followMap = serverFollowing.reduce((acc, id) => {
          if (id) acc[id] = true;
          return acc;
        }, {});

        setFollowing((prev) => ({ ...prev, ...followMap }));
      } catch (fetchError) {
        console.error("Failed to load current follow list:", fetchError);
      }
    };

    loadCurrentFollowList();
  }, [user?.token, user?.username, user?.following]);

  // Handler for follow/unfollow
  const handleFollow = async (short) => {
    if (!short?.userId || !user?.token) return;
    const currentlyFollowing = !!following[short.userId];

    setFollowLoading((prev) => ({ ...prev, [short.userId]: true }));
    setFollowing((prev) => ({ ...prev, [short.userId]: !currentlyFollowing }));

    try {
      const result = currentlyFollowing
        ? await unfollow(short.userId, user.token)
        : await follow(short.userId, user.token);

      if (result !== "ok") {
        throw new Error(result || "Follow update failed");
      }

      const storedUser = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") || "null");
        } catch (err) {
          return null;
        }
      })();

      if (storedUser) {
        const updatedFollowing = Array.isArray(storedUser.following) ? [...storedUser.following] : [];
        if (currentlyFollowing) {
          const index = updatedFollowing.findIndex((id) => String(id) === String(short.userId));
          if (index >= 0) updatedFollowing.splice(index, 1);
        } else if (!updatedFollowing.some((id) => String(id) === String(short.userId))) {
          updatedFollowing.push(short.userId);
        }
        localStorage.setItem("user", JSON.stringify({ ...storedUser, following: updatedFollowing }));
      }
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || String(error);
      console.error("Follow/unfollow failed:", message);
      const fallbackState = message.toLowerCase().includes("already following")
        ? true
        : message.toLowerCase().includes("not following")
        ? false
        : currentlyFollowing;

      setFollowing((prev) => ({ ...prev, [short.userId]: fallbackState }));
    } finally {
      setFollowLoading((prev) => ({ ...prev, [short.userId]: false }));
    }
  };

  // Handler for saving shorts
  const handleSaveShort = (short) => {
    const isAlreadySaved = savedShorts.some(saved => saved.url === short.url);
    if (!isAlreadySaved) {
      const newSavedShorts = [...savedShorts, short];
      setSavedShorts(newSavedShorts);
      // Persist to localStorage
      try {
        localStorage.setItem('savedShorts', JSON.stringify(newSavedShorts));
        showReelSavedNotification('Reel saved!');
      } catch (error) {
        console.error('Error saving to localStorage:', error);
        showReelSavedNotification('Reel saved locally!');
      }
    } else {
      showReelSavedNotification('Reel already saved!');
    }
  };

  // Handler for removing saved shorts
  const handleRemoveSavedShort = (shortUrl) => {
    const newSavedShorts = savedShorts.filter(saved => saved.url !== shortUrl);
    setSavedShorts(newSavedShorts);
    // Persist to localStorage
    try {
      localStorage.setItem('savedShorts', JSON.stringify(newSavedShorts));
      showReelSavedNotification('Reel removed from saved!');
    } catch (error) {
      console.error('Error updating localStorage:', error);
    }
  };

  // Sync savedShorts with localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('savedShorts', JSON.stringify(savedShorts));
    } catch (error) {
      console.error('Error syncing savedShorts to localStorage:', error);
    }
  }, [savedShorts]);

  return (
    <>
      <Header
        showMessengerSidebar={false}
        onMessengerToggle={() => { }}
      />
      <div className="watch-container"
        style={{
          minHeight: "100vh",
          minWidth: "100vw",
          width: "100vw",
          height: "100vh",
          display: "flex",
          flexDirection: "row",
          background: "#f9c5da",
          margin: 0,
          padding: 0,
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1,
        }}
      >
        {/* Sidebar */}
        <div className="watch-sidebar" style={{
          width: 220,
          background: "#f8daf0",
          height: "100%",
          boxShadow: "2px 0 8px #0001",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          padding: "100px 0 0 0"
        }}>
          <div className="watch-nav-buttons">
            <button 
              className={`watch-nav-button ${category === "all" ? "active" : ""}`}
              onClick={() => handleCategoryChange("all")}
            style={{
              width: "100%",
              background: category === "all" ? "#f9c5da" : "#f8daf0",
              color: category === "all" ? "#2c3e50" : "#2c3e50",
              border: "none",
              borderRadius: 0,
              padding: "18px 24px",
              fontWeight: 600,
              fontSize: "1.1rem",
              textAlign: "left",
              cursor: "pointer"
            }}>
            📻All Videos
          </button>
            <button 
              className={`watch-nav-button ${category === "shorts" ? "active" : ""}`}
              onClick={() => handleCategoryChange("shorts")}
            style={{
              width: "100%",
              background: category === "shorts" ? "#f9c5da" : "#f8daf0",
              color: category === "shorts" ? "#2c3e50" : "#2c3e50",
              border: "none",
              borderRadius: 0,
              padding: "18px 24px",
              fontWeight: 600,
              fontSize: "1.1rem",
              textAlign: "left",
              cursor: "pointer"
            }}>
            ⚡Shorts
          </button>
            <button 
              className={`watch-nav-button ${category === "movies" ? "active" : ""}`}
              onClick={() => handleCategoryChange("movies")}
            style={{
              width: "100%",
              background: category === "movies" ? "#f9c5da" : "#f8daf0",
              color: category === "movies" ? "#2c3e50" : "#2c3e50",
              border: "none",
              borderRadius: 0,
              padding: "18px 24px",
              fontWeight: 600,
              fontSize: "1.1rem",
              textAlign: "left",
              cursor: "pointer"
            }}>
            🎬Movies
          </button>
            <button 
              className={`watch-nav-button ${category === "saved" ? "active" : ""}`}
              onClick={() => {
                // Navigate to saved shorts page
                setCategory("saved");
              }}
              style={{
                width: "100%",
                background: category === "saved" ? "#f9c5da" : "#f8daf0",
                color: category === "saved" ? "#2c3e50" : "#2c3e50",
                border: "none",
                borderRadius: 0,
                padding: "18px 24px",
                fontWeight: 600,
                fontSize: "1.1rem",
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}
              onMouseEnter={(e) => {
                if (category !== "saved") {
                  e.currentTarget.style.background = "#f9c5da";
                }
              }}
              onMouseLeave={(e) => {
                if (category !== "saved") {
                  e.currentTarget.style.background = "#f8daf0";
                }
              }}
            >
              <FaBookmark size={16} />
              Saved reels
            </button>
          </div>
        </div>
                 {/* Main content */}
         <div className="watch-main-content" style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
          {/* Back button for shorts page */}
          <button
            className="shorts-back-button"
            onClick={() => {
              if (category === "shorts") {
                setCategory("all");
              } else if (window.history.length > 1) {
                window.history.back();
              } else {
                navigate('/');
              }
            }}
            title="Go back"
            aria-label="Go back"
          >
            ←
          </button>

          {/* Go Back button for Movies page - Mobile Only */}
          {category === "movies" && (
            <button
              onClick={handleMoviesGoBack}
              className="movies-back-button notranslate"
              translate="no"
              style={{
                position: "absolute",
                top: 65,
                left: 20,
                zIndex: 10,
                background: "rgba(0,0,0,0.7)",
                color: "#fff",
                border: "none",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "20px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
              }}
            >
              <span className="notranslate" translate="no">←</span>
            </button>
          )}
          <div className="watch-title" style={{ fontWeight: "bold", fontSize: "1.5rem", margin: "50px 0px 20px" }}>
            {category === "all" ? "All Videos" : category === "shorts" ? "Shorts" : category === "movies" ? "Movies" : "Saved Shorts"}
          </div>
          {loading ? (
            <div>Loading videos...</div>
          ) : error ? (
            <div style={{ color: "red" }}>{error}</div>
          ) : displayVideos.length === 0 ? (
            <div>No videos found.</div>
          ) : category === "saved" ? (
            // Saved Shorts page
            <div style={{
              width: "90vw",
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "20px"
            }}>
              {savedShorts.length === 0 ? (
                <div style={{
                  textAlign: "center",
                  padding: "60px 20px",
                  color: "#666",
                  fontSize: "18px"
                }}>
                  <FaBookmark size={48} style={{ marginBottom: "20px", opacity: 0.5 }} />
                  <div>No saved shorts yet</div>
                  <div style={{ fontSize: "14px", marginTop: "8px", opacity: 0.7 }}>
                    {/* Save shorts by clicking the three dots menu and selecting "Save reel" */}
                  </div>
                </div>
              ) : (
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "20px"
                }}>
                  {savedShorts.map((short, index) => (
                    <div
                      key={short.url}
                      style={{
                        position: "relative",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: "#fff",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                        cursor: "pointer"
                      }}
                    >
                      {/* Remove button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveSavedShort(short.url);
                        }}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "rgba(255,255,255,0.9)",
                          border: "none",
                          borderRadius: "50%",
                          width: "32px",
                          height: "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          zIndex: 10,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,1)";
                          e.currentTarget.style.transform = "scale(1.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "rgba(255,255,255,0.9)";
                          e.currentTarget.style.transform = "scale(1)";
                        }}
                      >
                        {/* Unsave bookmark icon */}
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          {/* Bookmark shape */}
                          <path
                            d="M19 21L12 16L5 21V5C5 3.89543 5.89543 3 7 3H17C18.1046 3 19 3.89543 19 5V21Z"
                            stroke="#ff4444"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                          />
                          {/* Diagonal line */}
                          <path
                            d="M8 8L16 16M16 8L8 16"
                            stroke="#ff4444"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                      
                      <div
                        onClick={() => {
                          // Find the short in the main shorts array and navigate to it
                          const shortIndex = shorts.findIndex(s => s.url === short.url);
                          if (shortIndex !== -1) {
                            setHasUserInteracted(true);
                            setCategory("shorts");
                            setShortIndex(shortIndex);
                          }
                        }}
                      >
                      <video
                        src={short.url}
                        style={{
                          width: "100%",
                          height: "200px",
                          objectFit: "cover"
                        }}
                        poster="/images/video-poster.png"
                        muted
                        preload="metadata"
                        onMouseOver={e => { e.currentTarget.play().catch(() => {}); }}
                        onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                      />
                      <div style={{
                        padding: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}>
                        {short.profile ? (
                          <img
                            src={short.profile}
                            alt="profile"
                            style={{
                              width: "24px",
                              height: "24px",
                              borderRadius: "50%",
                              objectFit: "cover"
                            }}
                          />
                        ) : (
                          <div style={{
                            width: "24px",
                            height: "24px",
                            borderRadius: "50%",
                            background: "#ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: "12px"
                          }}>
                            {short.username?.[0]?.toUpperCase() || "U"}
                          </div>
                        )}
                        <span style={{ fontSize: "14px", fontWeight: 500, color: "#333" }}>
                          @{short.username}
                        </span>
                      </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : category === "shorts" ? (
            // Shorts vertical scroll view - with gaps for mobile
            <div
              id="shorts-scroll-container"
              className="shorts-scroll-container"
              style={{
                display: "flex",
                flexDirection: "column",
                height: "80vh",
                width: "100%",
                maxWidth: "350px",
                position: "relative",
                background: "#2221",
                borderRadius: "16px",
                boxShadow: "0 2px 12px #0002",
                margin: "0 auto",
                overflowY: "auto",
                scrollSnapType: "y mandatory"
              }}
            >
              {shorts.map((short, index) => (
                <React.Fragment key={short.url}>
                  <div
                    className="shorts-item-container"
                    style={{
                      width: "100%",
                      height: "100vh",
                      minHeight: "600px",
                      position: "relative",
                      scrollSnapAlign: "start",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center"
                    }}>
                    <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{
                        position: "relative",
                        width: "min(320px, 90vw, 56vh)",
                        height: "calc(min(320px, 90vw, 56vh) * 16 / 9)",
                        aspectRatio: "9/16"
                      }}>
                        <div style={{ position: "relative", width: "100%", height: "100%" }}>
                          <div style={{ position: "relative", width: "100%", height: "100%" }}>
                            <video
                              ref={index === shortIndex ? shortVideoRef : null}
                              id={index === shortIndex ? "shorts-video" : `shorts-video-${index}`}
                              src={short.url}
                              controlsList="nofullscreen nodownload noremoteplayback nopictureinpicture"
                              disablePictureInPicture
                              loop
                              playsInline
                              muted={isMuted}
                              style={{
                                width: "100%",
                                height: "100%",
                                aspectRatio: "9/16",
                                objectFit: "cover",
                                borderRadius: "16px",
                                background: "#000",
                                boxShadow: "0 4px 24px #0003",
                                cursor: "pointer"
                              }}
                              poster="/images/video-poster.png"
                              onLoadedData={(e) => {
                                if (index === shortIndex && hasUserInteracted) {
                                  // Reset to beginning and play
                                  e.currentTarget.currentTime = 0;
                                  e.currentTarget.play().catch((error) => {
                                    console.log('Auto-play prevented on load:', error);
                                  });
                                }
                              }}
                              onClick={e => {
                                const vid = e.currentTarget;
                                if (vid.paused) {
                                  vid.play().catch(() => {});
                                } else {
                                  vid.pause();
                                }
                              }}
                            />
                            {/* Action buttons - Always visible on all shorts */}
                            <div className="action-buttons" style={{
                              position: "absolute",
                              top: "auto",
                              right: 10,
                              bottom: 88,
                              transform: "none",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 14,
                              zIndex: 4,
                              background: "rgba(0,0,0,0.18)",
                              borderRadius: 14,
                              padding: "8px 6px",
                              backdropFilter: "blur(2px)"
                            }}>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <button
                                  onClick={() => handleLike(short)}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: "transparent",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    padding: 0
                                  }}
                                >
                                  {engagementByPost[short.postId]?.likedByMe ? (
                                    <FaHeart size={24} color="#ff4d6d" />
                                  ) : (
                                    <FaRegHeart size={24} color="#fff" />
                                  )}
                                </button>
                                <span className="action-label" style={{ fontSize: 10, color: "#fff", fontWeight: 600, marginTop: 1, textShadow: "1px 1px 2px rgba(0,0,0,0.75)" }}>Likes</span>
                                <span className="action-label" style={{ fontSize: 11, color: "#fff", fontWeight: 700, marginTop: 1, textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}>{engagementByPost[short.postId]?.likesCount || 0}</span>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <button
                                  onClick={() => setShowComments(true)}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: "transparent",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    padding: 0
                                  }}
                                >
                                  <FaComment size={23} color="#fff" />
                                </button>
                                <span className="action-label" style={{ fontSize: 11, color: "#fff", fontWeight: 700, marginTop: 1, textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}>{(engagementByPost[short.postId]?.comments || []).length}</span>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <button
                                  onClick={() => handleShare(short.url)}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: "transparent",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    padding: 0
                                  }}
                                >
                                  <FaShare size={22} color="#fff" />
                                </button>
                                <span className="action-label" style={{ fontSize: 11, color: "#fff", fontWeight: 700, marginTop: 1, textShadow: "1px 1px 2px rgba(0,0,0,0.8)" }}>{shares[short.url] || 0}</span>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <button
                                  onClick={() => {
                                    const isSaved = savedShorts.some(saved => saved.url === short.url);
                                    if (isSaved) {
                                      handleRemoveSavedShort(short.url);
                                    } else {
                                      handleSaveShort(short);
                                    }
                                  }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: "transparent",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    padding: 0
                                  }}
                                  title="Save reel"
                                >
                                  <FaBookmark size={20} color={savedShorts.some(saved => saved.url === short.url) ? "#4ea1ff" : "#fff"} />
                                </button>
                              </div>
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setShowShortsMenu({ short, position: { x: rect.left - 120, y: rect.bottom + 5 } });
                                  }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    background: "transparent",
                                    border: "none",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "transform 0.2s",
                                    padding: 0
                                  }}
                                >
                                  <FaEllipsisH size={16} color="#fff" />
                                </button>
                              </div>
                            </div>

                            {/* User info overlay - Always visible on all shorts */}
                            <div style={{
                              position: "absolute",
                              left: 10,
                              bottom: 35,
                              display: "flex",
                              alignItems: "center",
                              background: "rgba(0,0,0,0.55)",
                              borderRadius: 16,
                              padding: "3px 8px 3px 3px",
                              zIndex: 3
                            }}>
                              {short.profile ? (
                                <img src={short.profile} alt="profile" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", marginRight: 6, border: "1px solid #fff" }} />
                              ) : (
                                <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#ccc", marginRight: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12 }}>
                                  {short.username?.[0]?.toUpperCase() || "U"}
                                </div>
                              )}
                              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                                <span
                                  style={{ color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", textDecoration: "underline" }}
                                  onClick={() => navigate(`/profile/${short.username}`)}
                                >
                                  @{short.username}
                                </span>
                                {short.username !== user?.username && (
                                  <button
                                    onClick={() => handleFollow(short)}
                                    disabled={followLoading[short.userId]}
                                    style={{
                                      background: following[short.userId] ? "#666" : "#1976d2",
                                      color: "#fff",
                                      border: "1px solid rgba(255,255,255,0.7)",
                                      borderRadius: 8,
                                      padding: "1px 8px",
                                      fontSize: 10,
                                      fontWeight: 600,
                                      cursor: followLoading[short.userId] ? "not-allowed" : "pointer",
                                      opacity: followLoading[short.userId] ? 0.7 : 1,
                                      marginTop: 1,
                                      transition: "background 0.2s, opacity 0.2s"
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = following[short.userId] ? "#555" : "#1565c0";
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = following[short.userId] ? "#666" : "#1976d2";
                                    }}
                                  >
                                    {following[short.userId] ? "Following" : "Follow"}
                                  </button>
                                )}
                              </div>
                            </div>

                            {/* Progress bar - Always visible on all shorts */}
                            <div style={{ position: "absolute", left: 10, bottom: 18, width: "calc(100% - 20px)", zIndex: 3 }}>
                              <VideoProgressBar 
                                videoId={index === shortIndex ? "shorts-video" : `shorts-video-${index}`}
                                containerStyle={{ width: "100%" }}
                              />
                            </div>

                            {index === shortIndex && (
                              <>
                                <div style={{ position: "absolute", left: 16, top: 16, zIndex: 5 }}>
                                  <PlayPauseButton videoRef={shortVideoRef} />
                                </div>
                                <div style={{ position: "absolute", right: 16, top: 16, zIndex: 5 }}>
                                  <button
                                    onClick={() => setIsMuted(m => !m)}
                                    style={{
                                      background: "#222",
                                      color: "#fff",
                                      border: "none",
                                      borderRadius: "50%",
                                      width: 32,
                                      height: 32,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 18,
                                      boxShadow: "0 2px 8px #0002",
                                      cursor: "pointer"
                                    }}
                                    title={isMuted ? "Unmute" : "Mute"}
                                  >
                                    {isMuted ? "🔇" : "🔊"}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Comments Modal */}
                    {showComments && index === shortIndex && (
                      <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.6)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        paddingRight: "clamp(8px, 8vw, 120px)",
                        paddingLeft: 8,
                        zIndex: 1000
                      }}>
                        <div style={{ background: "#1b1f27", borderRadius: 28, minWidth: 320, maxWidth: 460, width: "min(460px, 96vw)", maxHeight: "86vh", boxShadow: "0 18px 45px rgba(0,0,0,0.48)", border: "1px solid rgba(255,255,255,0.08)", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                          <div style={{ display: "grid", gridTemplateColumns: "48px 1fr 48px", alignItems: "center", padding: "14px 14px 10px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                            <button
                              onClick={() => setShowComments(false)}
                              title="Close comments"
                              style={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                border: "none",
                                background: "transparent",
                                color: "#f2f2f2",
                                fontSize: 28,
                                fontWeight: 400,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                lineHeight: 1,
                              }}
                            >
                              X
                            </button>
                            <div style={{ fontWeight: 700, fontSize: 20, color: "#f4f6fb", lineHeight: 1, textAlign: "center" }}>Comments</div>
                            <div />
                          </div>
                          <div style={{ padding: "12px 14px", overflowY: "auto", flex: 1 }}>
                            {((engagementByPost[shorts[shortIndex]?.postId]?.comments) || []).length === 0 ? (
                              <div style={{ color: "#a8afba", padding: "12px 4px" }}>No comments yet.</div>
                            ) : (
                              ((engagementByPost[shorts[shortIndex]?.postId]?.comments) || []).map((c) => (
                                <div key={c.id} style={{ display: "flex", gap: 10, marginBottom: 14, color: "#eef1f6" }}>
                                  <div style={{ width: 34, height: 34, borderRadius: "50%", background: "#5d6572", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0 }}>
                                    {(c.displayName || "U")[0]?.toUpperCase()}
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 15, lineHeight: 1.2 }}>
                                      <strong style={{ color: "#ffffff" }}>{c.displayName}</strong>
                                      <span style={{ color: "#9aa3b2", marginLeft: 8, fontSize: 13 }}>{formatCommentAge(c.commentAt)}</span>
                                    </div>
                                    <div style={{ marginTop: 2, fontSize: 15, color: "#e4e8ef", wordBreak: "break-word" }}>{c.text}</div>
                                    <button
                                      onClick={() =>
                                        setShowReplyForComment((prev) => (prev === c.id ? null : c.id))
                                      }
                                      style={{
                                        marginTop: 4,
                                        padding: 0,
                                        border: "none",
                                        background: "transparent",
                                        fontSize: 13,
                                        color: "#9aa3b2",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Reply
                                    </button>

                                    {(c.replies || []).length > 0 && (
                                      <div style={{ marginTop: 8, paddingLeft: 10, borderLeft: "1px solid rgba(255,255,255,0.15)" }}>
                                        {(c.replies || []).map((r) => (
                                          <div key={r.id} style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 13, color: "#ffffff" }}>
                                              <strong>{r.displayName}</strong>
                                              <span style={{ color: "#9aa3b2", marginLeft: 6, fontSize: 12 }}>{formatCommentAge(r.commentAt)}</span>
                                            </div>
                                            <div style={{ fontSize: 13, color: "#d8dce4", wordBreak: "break-word" }}>{r.text}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {showReplyForComment === c.id && (
                                      <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
                                        <input
                                          type="text"
                                          value={replyDraftByComment[c.id] || ""}
                                          onChange={(e) =>
                                            setReplyDraftByComment((prev) => ({
                                              ...prev,
                                              [c.id]: e.target.value,
                                            }))
                                          }
                                          onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                              e.preventDefault();
                                              handleReplyToComment(shorts[shortIndex], c.id);
                                            }
                                          }}
                                          placeholder="Write a reply..."
                                          style={{
                                            flex: 1,
                                            padding: "8px 10px",
                                            borderRadius: 999,
                                            border: "1px solid rgba(255,255,255,0.16)",
                                            background: "#242a35",
                                            color: "#f5f7fb",
                                            fontSize: 13,
                                            outline: "none",
                                          }}
                                        />
                                        <button
                                          onClick={() => handleReplyToComment(shorts[shortIndex], c.id)}
                                          style={{
                                            border: "none",
                                            background: "#2e6ee6",
                                            color: "#fff",
                                            borderRadius: 999,
                                            padding: "8px 12px",
                                            fontSize: 12,
                                            cursor: "pointer",
                                          }}
                                        >
                                          Reply
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <button
                                    onClick={() => handleToggleCommentLike(c.id)}
                                    style={{
                                      border: "none",
                                      background: "transparent",
                                      cursor: "pointer",
                                      padding: 0,
                                      alignSelf: "center",
                                    }}
                                    title="Like comment"
                                  >
                                    {likedComments[c.id] ? (
                                      <FaHeart size={16} color="#ff4d6d" />
                                    ) : (
                                      <FaRegHeart size={16} color="#9aa3b2" />
                                    )}
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                          <div style={{ padding: "10px 14px 14px", borderTop: "1px solid rgba(255,255,255,0.08)", display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#6a7383", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
                              {(user?.first_name || user?.username || "U")[0]?.toUpperCase()}
                            </div>
                            <input
                              type="text"
                              value={newComment}
                              onChange={e => setNewComment(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleAddComment(shorts[shortIndex]);
                                }
                              }}
                              placeholder="Add a comment..."
                              style={{ flex: 1, padding: "10px 14px", borderRadius: 999, border: "1px solid rgba(255,255,255,0.14)", background: "#242a35", color: "#f5f7fb", fontSize: 16, outline: "none" }}
                            />
                            <button onClick={() => handleAddComment(shorts[shortIndex])} style={{ border: "none", background: "transparent", color: "#f5f7fb", fontSize: 24, cursor: "pointer", padding: "2px 4px" }} title="Post comment">➤</button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Share Dialog */}
                    {showShareDialog && (
                      <div style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(0,0,0,0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000
                      }}>
                        <div style={{ 
                          background: "#fff", 
                          borderRadius: 12, 
                          padding: 24, 
                          minWidth: 320, 
                          maxWidth: 400, 
                          boxShadow: "0 2px 12px rgba(0,0,0,0.3)" 
                        }}>
                          <div style={{ 
                            display: "flex", 
                            justifyContent: "space-between", 
                            alignItems: "center", 
                            marginBottom: 20 
                          }}>
                            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Share</h3>
                            <button 
                              onClick={() => setShowShareDialog(false)}
                              style={{ 
                                background: "none", 
                                border: "none", 
                                fontSize: 20, 
                                cursor: "pointer",
                                color: "#666"
                              }}
                            >
                              ×
                            </button>
                          </div>

                          <div style={{ borderTop: "1px solid #eee", paddingTop: 20 }}>
                            <div style={{ 
                              display: "flex", 
                              gap: 16, 
                              flexWrap: "nowrap",
                              overflowX: "auto",
                              padding: "0 8px",
                              scrollBehavior: "smooth",
                              WebkitOverflowScrolling: "touch",
                              width: "100%",
                              maxWidth: "350px"
                            }}>
                              <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                gap: 8,
                                cursor: "pointer",
                                flexShrink: 0
                              }}
                              onClick={() => handleMessengerShare(shorts[shortIndex]?.url || '')}
                              >
                                <div style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: "50%", 
                                  background: "#006AFF", 
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center",
                                  color: "#fff"
                                }}>
                                  <FaBolt size={20} />
                                </div>
                                <span style={{ fontSize: 12, textAlign: "center" }}>Messenger</span>
                              </div>
                              
                              <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                gap: 8,
                                cursor: "pointer",
                                flexShrink: 0
                              }}
                              onClick={() => handleWhatsAppShare(shorts[shortIndex]?.url || '')}
                              >
                                <div style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: "50%", 
                                  background: "#25D366", 
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center",
                                  color: "#fff"
                                }}>
                                  <FaWhatsapp size={20} />
                                </div>
                                <span style={{ fontSize: 12, textAlign: "center" }}>WhatsApp</span>
                              </div>
                              
                              <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                gap: 8,
                                cursor: "pointer",
                                flexShrink: 0
                              }}
                              onClick={() => handleStoryShare(shorts[shortIndex]?.url || '')}
                              >
                                <div style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: "50%", 
                                  background: "#fff", 
                                  border: "2px solid #666",
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center",
                                  color: "#666"
                                }}>
                                  <FaBook size={20} />
                                </div>
                                <span style={{ fontSize: 12, textAlign: "center" }}>Your story</span>
                              </div>
                              
                              <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                gap: 8,
                                cursor: "pointer",
                                background: "#f0f0f0",
                                borderRadius: 8,
                                padding: "8px 12px",
                                flexShrink: 0
                              }}
                              onClick={() => handleActualShare(shorts[shortIndex]?.url || '')}
                              >
                                <div style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: "50%", 
                                  background: "#fff", 
                                  border: "2px solid #666",
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center",
                                  color: "#666"
                                }}>
                                  <FaLink size={20} />
                                </div>
                                <span style={{ fontSize: 12, textAlign: "center" }}>Copy link</span>
                              </div>
                              
                              <div style={{ 
                                display: "flex", 
                                flexDirection: "column", 
                                alignItems: "center", 
                                gap: 8,
                                cursor: "pointer",
                                flexShrink: 0
                              }}
                              onClick={() => handleFriendsShare(shorts[shortIndex]?.url || '')}
                              >
                                <div style={{ 
                                  width: 48, 
                                  height: 48, 
                                  borderRadius: "50%", 
                                  background: "#fff", 
                                  border: "2px solid #666",
                                  display: "flex", 
                                  alignItems: "center", 
                                  justifyContent: "center",
                                  color: "#666"
                                }}>
                                  <FaUsers size={20} />
                                </div>
                                <span style={{ fontSize: 12, textAlign: "center" }}>Friend's profile</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          ) : (
            <>
              {/* Shorts Row - only show on "all" category */}
              {shorts.length > 0 && category === "all" && (
                <div className="shorts-container" style={{
                  width: "90vw",
                  maxWidth: "1200px",
                  margin: "0 auto 32px auto",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  paddingBottom: 8,
                  background: "transparent",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "none"
                }}>
                  <div style={{ fontWeight: 600, fontSize: 18, margin: "0 0 10px 8px" }}>Shorts</div>
                  <div className="shorts-row" style={{ display: "flex", flexDirection: "row", gap: 16 }}>
                    {shorts.map((video, idx) => {
                      const shortsIdx = shorts.findIndex(s => s.url === video.url);
                      return (
                        <div
                          key={video.url}
                          className="shorts-item"
                          style={{ position: "relative", width: 240, height: 340, borderRadius: "12px", padding: "10px", display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", background: "transparent", boxShadow: "0 2px 10px #0002", flexShrink: 0 }}
                          onClick={() => {
                            setHasUserInteracted(true);
                            setCategory("shorts");
                            setShortIndex(shortsIdx !== -1 ? shortsIdx : 0);
                          }}
                        >
                          <video
                            src={video.url}
                            className="shorts-video"
                            style={{ width: "100%", height: 320, borderRadius: "10px", objectFit: "cover" }}
                            poster="/images/video-poster.png"
                            controlsList="nopictureinpicture"
                            disablePictureInPicture
                            muted={!!videoMute[video.url]}
                            preload={category === "all" ? "auto" : "metadata"}
                            onMouseOver={e => { e.currentTarget.play().catch(() => {}); }}
                            onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                            onLoadedData={(e) => {
                              // Auto-play first frame when loaded if category is "all"
                              if (category === "all") {
                                e.currentTarget.currentTime = 0.1;
                                e.currentTarget.pause();
                              }
                            }}
                          />
                          {/* Mute/Unmute button top-right */}
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setVideoMute(prev => ({ ...prev, [video.url]: !prev[video.url] }));
                            }}
                            className="shorts-mute-button"
                            style={{
                              position: "absolute",
                              right: 18,
                              top: 18,
                              background: "#222",
                              color: "#fff",
                              border: "none",
                              borderRadius: "50%",
                              width: 36,
                              height: 36,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 18,
                              boxShadow: "0 2px 8px #0002",
                              cursor: "pointer",
                              zIndex: 2
                            }}
                            title={videoMute[video.url] ? "Unmute" : "Mute"}
                          >
                            {videoMute[video.url] ? "🔇" : "🔊"}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Movies Row */}
              {movies.length > 0 && category === "all" && (
                 <div className="movies-row" style={{
                  width: "90vw",
                  maxWidth: "1200px",
                  margin: "0 auto 32px auto",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  paddingBottom: 8,
                  background: "transparent",
                  borderRadius: "12px",
                  padding: "16px",
                  border: "none"
                }}>
                  <div style={{ fontWeight: 600, fontSize: 18, margin: "0 0 10px 8px" }}>Movies</div>
                   <div className="movies-row-content" style={{ display: "flex", flexDirection: "row", gap: 16 }}>
                    {movies.map((video, idx) => (
                      <div
                        key={video.url}
                         className="movie-item"
                        style={{ 
                          position: "relative", 
                          minWidth: 280, 
                          maxWidth: 320, 
                          borderRadius: "12px", 
                          padding: "10px", 
                          display: "flex", 
                          flexDirection: "column", 
                          alignItems: "center", 
                          cursor: "pointer", 
                          background: "#fff", 
                          boxShadow: "0 2px 10px #0002"
                        }}
                        onClick={() => {
                          setFullScreenVideo(video);
                        }}
                      >
                        <video
                          src={video.url}
                          style={{ width: "100%", height: 180, borderRadius: "10px", objectFit: "cover" }}
                          poster="/images/video-poster.png"
                          controlsList="nopictureinpicture"
                          disablePictureInPicture
                          muted={!!videoMute[video.url]}
                          preload={category === "all" ? "auto" : "metadata"}
                          onMouseOver={e => { e.currentTarget.play().catch(() => {}); }}
                          onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                          onLoadedData={(e) => {
                            // Auto-play first frame when loaded if category is "all"
                            if (category === "all") {
                              e.currentTarget.currentTime = 0.1;
                              e.currentTarget.pause();
                            }
                          }}
                        />
                        {/* Mute/Unmute button top-right */}
                        <button
                          onClick={e => {
                            e.stopPropagation();
                            setVideoMute(prev => ({ ...prev, [video.url]: !prev[video.url] }));
                          }}
                          style={{
                            position: "absolute",
                            right: 18,
                            top: 18,
                            background: "#222",
                            color: "#fff",
                            border: "none",
                            borderRadius: "50%",
                            width: 36,
                            height: 36,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 18,
                            boxShadow: "0 2px 8px #0002",
                            cursor: "pointer",
                            zIndex: 2
                          }}
                          title={videoMute[video.url] ? "Unmute" : "Mute"}
                        >
                          {videoMute[video.url] ? "🔇" : "🔊"}
                        </button>
                        {/* Duration badge */}
                        <div style={{
                          position: "absolute",
                          bottom: 18,
                          right: 18,
                          background: "rgba(0,0,0,0.8)",
                          color: "#fff",
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 12,
                          fontWeight: 500
                        }}>
                          {videoDurations[video.url] ? `${Math.floor(videoDurations[video.url] / 60)}:${Math.floor(videoDurations[video.url] % 60).toString().padStart(2, '0')}` : ''}
                        </div>
                        {/* User info */}
                        <div style={{ marginTop: 8, display: "flex", alignItems: "center", width: "100%" }}>
                          {video.profile ? (
                            <img src={video.profile} alt="profile" style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover", marginRight: 8 }} />
                          ) : (
                            <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#ccc", marginRight: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12 }}>
                              {video.username?.[0]?.toUpperCase() || "U"}
                            </div>
                          )}
                          <span style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>@{video.username}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Movies Grid View (when movies category is selected) */}
              {category === "movies" && (
                 <div className="movies-grid" style={{
                  width: "90vw",
                  maxWidth: "1200px",
                  margin: "0 auto",
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                  gap: 20,
                  padding: "0 20px"
                }}>
                  {movies.map((video, idx) => (
                    <div
                      key={video.url}
                       className="movie-grid-item"
                      style={{ position: "relative", borderRadius: "12px", padding: "10px", display: "flex", flexDirection: "column", cursor: "pointer", background: "#fff", boxShadow: "0 2px 10px #0002" }}
                      onClick={() => {
                        setFullScreenVideo(video);
                      }}
                    >
                      <video
                        src={video.url}
                        style={{ width: "100%", height: 200, borderRadius: "10px", objectFit: "cover" }}
                        poster="/images/video-poster.png"
                        controlsList="nopictureinpicture"
                        disablePictureInPicture
                        muted={!!videoMute[video.url]}
                        preload={category === "movies" ? "auto" : "metadata"}
                        onMouseOver={e => { e.currentTarget.play().catch(() => {}); }}
                        onMouseOut={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                        onLoadedData={(e) => {
                          // Auto-play first frame when loaded if category is "movies"
                          if (category === "movies") {
                            e.currentTarget.currentTime = 0.1;
                            e.currentTarget.pause();
                          }
                        }}
                      />
                      {/* Mute/Unmute button top-right */}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setVideoMute(prev => ({ ...prev, [video.url]: !prev[video.url] }));
                        }}
                        style={{
                          position: "absolute",
                          right: 18,
                          top: 18,
                          background: "#222",
                          color: "#fff",
                          border: "none",
                          borderRadius: "50%",
                          width: 36,
                          height: 36,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 18,
                          boxShadow: "0 2px 8px #0002",
                          cursor: "pointer",
                          zIndex: 2
                        }}
                        title={videoMute[video.url] ? "Unmute" : "Mute"}
                      >
                        {videoMute[video.url] ? "🔇" : "🔊"}
                      </button>
                      {/* Duration badge */}
                      <div style={{
                        position: "absolute",
                        bottom: 78,
                        right: 18,
                        background: "rgba(0,0,0,0.8)",
                        color: "#fff",
                        padding: "2px 6px",
                        borderRadius: 4,
                        fontSize: 12,
                        fontWeight: 500
                      }}>
                        {videoDurations[video.url] ? `${Math.floor(videoDurations[video.url] / 60)}:${Math.floor(videoDurations[video.url] % 60).toString().padStart(2, '0')}` : ''}
                      </div>
                      {/* User info */}
                      <div style={{ marginTop: 12, display: "flex", alignItems: "center", width: "100%" }}>
                        {video.profile ? (
                          <img src={video.profile} alt="profile" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover", marginRight: 10 }} />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "#ccc", marginRight: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 16 }}>
                            {video.username?.[0]?.toUpperCase() || "U"}
                          </div>
                        )}
                        <span style={{ fontSize: 16, fontWeight: 500, color: "#333" }}>@{video.username}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* If no videos found for current category */}
              {((category === "shorts" && shorts.length === 0) ||
                (category === "movies" && movies.length === 0) ||
                (category === "all" && allVideos.length === 0)) && (
                  <div>No videos found.</div>
                )}
            </>
          )}

        </div>
      </div>

      {/* Full Screen Video Player Modal - YouTube Style */}
      {fullScreenVideo && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "#0f0f0f",
          display: "flex",
          flexDirection: isMobileViewport ? "column" : "row",
          overflowY: isMobileViewport ? "auto" : "hidden",
          zIndex: 2000
        }}>
          {/* Close button */}
          <button
            onClick={() => setFullScreenVideo(null)}
            style={{
              position: "absolute",
              top: 20,
              left: 20,
              background: "rgba(255,255,255,0.1)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: 40,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              cursor: "pointer",
              zIndex: 2001,
              backdropFilter: "blur(10px)"
            }}
            title="Close"
          >
            ✕
          </button>

          {/* Main video section */}
          <div style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: isMobileViewport ? "12px" : "20px",
            paddingRight: isMobileViewport ? "12px" : "10px"
          }}>
            {/* Video player */}
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "20px"
            }}>
              <video
                src={fullScreenVideo.url}
                controls
                autoPlay
                controlsList="nopictureinpicture"
                disablePictureInPicture
                style={{
                  width: "100%",
                  height: "100%",
                  maxHeight: isMobileViewport ? "48vh" : "70vh",
                  objectFit: "contain",
                  borderRadius: "8px"
                }}
                onEnded={() => {
                  // Auto-play next video if available
                  const currentIndex = movies.findIndex(m => m.url === fullScreenVideo.url);
                  if (currentIndex !== -1 && currentIndex < movies.length - 1) {
                    setFullScreenVideo(movies[currentIndex + 1]);
                  }
                }}
              />
            </div>

            {/* Video info */}
            <div style={{
              background: "rgba(255,255,255,0.05)",
              borderRadius: "12px",
              padding: "20px",
              marginBottom: isMobileViewport ? "12px" : "20px"
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "15px"
              }}>
                {fullScreenVideo.profile ? (
                  <img
                    src={fullScreenVideo.profile}
                    alt="profile"
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      objectFit: "cover",
                      marginRight: 15,
                      border: "2px solid #fff"
                    }}
                  />
                ) : (
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    background: "#666",
                    marginRight: 15,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 20
                  }}>
                    {fullScreenVideo.username?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ color: "#fff", fontWeight: 600, fontSize: 18, marginBottom: "4px" }}>
                    @{fullScreenVideo.username}
                  </div>
                  <div style={{ color: "#aaa", fontSize: 14 }}>
                    Duration: {videoDurations[fullScreenVideo.url] ?
                      `${Math.floor(videoDurations[fullScreenVideo.url] / 60)}:${Math.floor(videoDurations[fullScreenVideo.url] % 60).toString().padStart(2, '0')}`
                      : 'Loading...'}
                  </div>
                </div>
              </div>
            </div>

            {/* Related movies for mobile (below user info) */}
            {isMobileViewport && (
              <div style={{
                background: "rgba(255,255,255,0.03)",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "12px",
                marginBottom: "12px"
              }}>
                <div style={{
                  color: "#fff",
                  fontSize: "16px",
                  fontWeight: 600,
                  marginBottom: "10px"
                }}>
                  Related Movies
                </div>

                {movies.filter(video => video.url !== fullScreenVideo.url).length === 0 ? (
                  <div style={{ color: "#666", fontSize: "13px", textAlign: "center", padding: "10px" }}>
                    No other movies available
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    {movies
                      .filter(video => video.url !== fullScreenVideo.url)
                      .slice(0, 5)
                      .map((video) => (
                        <div
                          key={video.url}
                          style={{
                            display: "flex",
                            cursor: "pointer",
                            padding: "8px",
                            borderRadius: "8px",
                            background: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(255,255,255,0.08)"
                          }}
                          onClick={() => setFullScreenVideo(video)}
                        >
                          <video
                            src={video.url}
                            style={{
                              width: "100px",
                              height: "56px",
                              objectFit: "cover",
                              borderRadius: "6px",
                              background: "#333"
                            }}
                            muted
                            poster="/images/video-poster.png"
                            controlsList="nopictureinpicture"
                            disablePictureInPicture
                          />
                          <div style={{ flex: 1, marginLeft: "10px", display: "flex", alignItems: "center" }}>
                            <span style={{ color: "#ddd", fontSize: "13px" }}>@{video.username}</span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Related videos sidebar */}
          {!isMobileViewport && (
          <div style={{
            width: "400px",
            height: "100vh",
            background: "rgba(255,255,255,0.03)",
            borderLeft: "1px solid rgba(255,255,255,0.1)",
            overflowY: "auto",
            padding: "20px 15px"
          }}>
            <div style={{
              color: "#fff",
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "20px",
              paddingLeft: "5px"
            }}>
              Related Movies
            </div>

            {/* Related videos list */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {movies
                .filter(video => video.url !== fullScreenVideo.url) // Exclude current video
                .slice(0, 10) // Show max 10 related videos
                .map((video, idx) => (
                  <div
                    key={video.url}
                    style={{
                      display: "flex",
                      cursor: "pointer",
                      padding: "8px",
                      borderRadius: "8px",
                      background: "rgba(255,255,255,0.05)",
                      transition: "background 0.2s",
                      border: "1px solid transparent"
                    }}
                    onClick={() => setFullScreenVideo(video)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.1)";
                      e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                      e.currentTarget.style.borderColor = "transparent";
                    }}
                  >
                    {/* Video thumbnail */}
                    <div style={{ position: "relative", minWidth: "120px", height: "68px" }}>
                      <video
                        src={video.url}
                        style={{
                          width: "120px",
                          height: "68px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          background: "#333"
                        }}
                        muted
                        poster="/images/video-poster.png"
                        controlsList="nopictureinpicture"
                        disablePictureInPicture
                      />
                      {/* Duration overlay */}
                      <div style={{
                        position: "absolute",
                        bottom: "4px",
                        right: "4px",
                        background: "rgba(0,0,0,0.8)",
                        color: "#fff",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontSize: "11px",
                        fontWeight: 500
                      }}>
                        {videoDurations[video.url] ?
                          `${Math.floor(videoDurations[video.url] / 60)}:${Math.floor(videoDurations[video.url] % 60).toString().padStart(2, '0')}`
                          : '0:00'}
                      </div>
                    </div>

                    {/* Video info */}
                    <div style={{
                      flex: 1,
                      marginLeft: "12px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between"
                    }}>
                      <div style={{
                        color: "#fff",
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "1.3",
                        marginBottom: "4px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                      }}>
                        Video by @{video.username}
                      </div>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "4px"
                      }}>
                        {video.profile ? (
                          <img
                            src={video.profile}
                            alt="profile"
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              objectFit: "cover",
                              marginRight: 8
                            }}
                          />
                        ) : (
                          <div style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            background: "#666",
                            marginRight: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 10
                          }}>
                            {video.username?.[0]?.toUpperCase() || "U"}
                          </div>
                        )}
                        <span style={{ color: "#aaa", fontSize: "12px" }}>
                          @{video.username}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Show message if no related videos */}
            {movies.filter(video => video.url !== fullScreenVideo.url).length === 0 && (
              <div style={{
                color: "#666",
                fontSize: "14px",
                textAlign: "center",
                marginTop: "40px",
                padding: "20px"
              }}>
                No other movies available
              </div>
            )}
          </div>
          )}
        </div>
      )}

      {/* Shorts Three Dots Menu */}
      {showShortsMenu && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "rgba(0,0,0,0.3)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          zIndex: 1000
        }}
        onClick={() => setShowShortsMenu(null)}
        >
          <div style={{
            position: "absolute",
            left: showShortsMenu.position.x,
            top: showShortsMenu.position.y,
            background: "#333",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            padding: "8px 0",
            minWidth: 160,
            zIndex: 1001
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                // Create shareable link with video ID
                const shareableUrl = `${window.location.origin}/watch?video=${showShortsMenu.short.postId}&type=shorts`;
                navigator.clipboard.writeText(shareableUrl);
                // Show a brief notification
                const notification = document.createElement('div');
                notification.style.cssText = `
                  position: fixed;
                  top: 20px;
                  right: 20px;
                  background: rgba(25, 118, 210, 0.9);
                  color: white;
                  padding: 8px 16px;
                  border-radius: 20px;
                  font-size: 14px;
                  font-weight: 500;
                  z-index: 10000;
                  animation: fadeInOut 2s ease-in-out;
                `;
                notification.textContent = '🔗 Link copied!';
                
                // Add CSS animation
                const style = document.createElement('style');
                style.textContent = `
                  @keyframes fadeInOut {
                    0% { opacity: 0; transform: translateY(-10px); }
                    20% { opacity: 1; transform: translateY(0); }
                    80% { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-10px); }
                  }
                `;
                document.head.appendChild(style);
                
                document.body.appendChild(notification);
                
                // Remove after animation
                setTimeout(() => {
                  if (notification.parentNode) {
                    notification.parentNode.removeChild(notification);
                  }
                  if (style.parentNode) {
                    style.parentNode.removeChild(style);
                  }
                }, 2000);
                setShowShortsMenu(null);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                border: "none",
                background: "#444",
                textAlign: "left",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                fontSize: 14,
                color: "#fff",
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#555";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#444";
              }}
            >
              <FaPaperclip size={14} />
              Copy link
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default Watch;
